import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface PrivacySection {
  id: string;
  title: string;
  icon: string;
  content: string[];
}

interface DataTable {
  headers: string[];
  rows: string[][];
}

@Component({
  selector: 'app-politique-confidentialite',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './politique-confidentialite.html',
  styleUrl: './politique-confidentialite.scss'
})
export class PolitiqueConfidentialite {
  lastUpdate = '1er mars 2026';

  dataTypes: DataTable = {
    headers: ['Type de données', 'Finalité', 'Base légale', 'Durée de conservation'],
    rows: [
      ['Données d\'identification (nom, prénom, email)', 'Création et gestion du compte', 'Exécution du contrat', '3 ans après la dernière activité'],
      ['Données de géolocalisation', 'Recherche "Autour de moi"', 'Consentement', 'Durée de la session'],
      ['Historique de navigation & favoris', 'Personnalisation de l\'expérience', 'Intérêt légitime', '12 mois'],
      ['Données de contact (messages)', 'Traitement des demandes', 'Exécution du contrat', '3 ans'],
      ['Données de connexion (logs, IP)', 'Sécurité et prévention des fraudes', 'Intérêt légitime', '12 mois'],
      ['Cookies analytiques', 'Mesure d\'audience anonymisée', 'Consentement', '13 mois maximum'],
    ]
  };

  cookieTypes = [
    {
      name: 'Cookies essentiels',
      required: true,
      description: 'Nécessaires au fonctionnement du site (session, authentification, sécurité). Ils ne peuvent pas être désactivés.',
      examples: 'Token de session, préférences RGPD'
    },
    {
      name: 'Cookies analytiques',
      required: false,
      description: 'Nous permettent de mesurer l\'audience et d\'améliorer le site. Toutes les données sont anonymisées.',
      examples: 'Google Analytics (anonymisé)'
    },
    {
      name: 'Cookies de préférences',
      required: false,
      description: 'Mémorisent vos choix pour personnaliser votre expérience (langue, filtres, etc.).',
      examples: 'Filtres de recherche, préférences d\'affichage'
    }
  ];

  rights = [
    { icon: '👁️', title: 'Droit d\'accès', desc: 'Obtenir une copie de toutes les données personnelles que nous détenons sur vous.' },
    { icon: '✏️', title: 'Droit de rectification', desc: 'Corriger toute donnée inexacte ou incomplète vous concernant.' },
    { icon: '🗑️', title: 'Droit à l\'effacement', desc: 'Demander la suppression de vos données ("droit à l\'oubli"), sous conditions.' },
    { icon: '📦', title: 'Droit à la portabilité', desc: 'Recevoir vos données dans un format structuré et lisible par machine.' },
    { icon: '🚫', title: 'Droit d\'opposition', desc: 'Vous opposer à un traitement basé sur notre intérêt légitime.' },
    { icon: '⏸️', title: 'Droit de limitation', desc: 'Demander la suspension temporaire du traitement de vos données.' },
  ];

  sections: PrivacySection[] = [
    {
      id: 'responsable',
      title: 'Responsable du traitement',
      icon: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
      content: [
        'Le responsable du traitement de vos données personnelles est :<br><strong>Referio SAS</strong>, 12 rue de l\'Innovation, 1000 Bruxelles, Belgique',
        'Numéro BCE : BE 0xxx.xxx.xxx — RPM Bruxelles',
        'Email du DPO (Délégué à la Protection des Données) : <a href="mailto:contact@referio.be">contact@referio.be</a>',
        'Vous pouvez exercer vos droits à tout moment en nous contactant à cette adresse.'
      ]
    },
    {
      id: 'collecte',
      title: 'Données collectées et traitements',
      icon: 'M4 7h16M4 12h16M4 17h10',
      content: [
        'Referio collecte uniquement les données strictement nécessaires à la fourniture de ses services. Le tableau ci-dessous détaille chaque traitement effectué, sa finalité, sa base légale et sa durée de conservation.',
        'Nous ne collectons jamais de données sensibles (origines raciales, opinions politiques, données de santé, etc.) au sens de l\'article 9 du RGPD.'
      ]
    },
    {
      id: 'destinataires',
      title: 'Destinataires des données',
      icon: 'M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0',
      content: [
        'Vos données sont accessibles uniquement aux personnes habilitées au sein de Referio, dans la stricte limite de leurs attributions.',
        'Nous faisons appel à des sous-traitants techniques pour l\'hébergement et l\'analyse d\'audience. Ces prestataires sont contractuellement engagés à respecter le RGPD et n\'utilisent vos données qu\'à notre demande et pour notre compte.',
        '<strong>Hébergement :</strong> OVHcloud (France/UE) — vos données restent hébergées au sein de l\'Union Européenne.',
        '<strong>Analytics :</strong> Google Analytics avec anonymisation de l\'IP activée — aucune donnée identifiable n\'est transmise à Google.',
        'Nous ne vendons jamais vos données à des tiers à des fins commerciales.'
      ]
    },
    {
      id: 'transferts',
      title: 'Transferts hors UE',
      icon: 'M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18',
      content: [
        'Vos données personnelles sont hébergées et traitées au sein de l\'Union Européenne.',
        'Dans le cas exceptionnel où un transfert hors UE serait nécessaire (ex. : certains outils tiers), nous nous assurons qu\'il est encadré par des garanties appropriées : décision d\'adéquation de la Commission européenne, clauses contractuelles types (CCT) ou règles d\'entreprise contraignantes.',
        'Vous pouvez nous demander à tout moment la liste des pays tiers concernés et les garanties mises en place.'
      ]
    },
    {
      id: 'droits',
      title: 'Vos droits',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      content: [
        'Conformément au RGPD et à la loi belge du 30 juillet 2018, vous disposez des droits suivants concernant vos données personnelles.',
        'Pour exercer l\'un de ces droits, envoyez votre demande à <a href="mailto:contact@referio.be">contact@referio.be</a> en joignant une copie de votre pièce d\'identité. Nous répondrons dans un délai maximum d\'<strong>un mois</strong>.',
        'Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de l\'<strong>Autorité de Protection des Données (APD)</strong> belge : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener">autoriteprotectiondonnees.be</a> — Rue de la Presse 35, 1000 Bruxelles.'
      ]
    },
    {
      id: 'cookies',
      title: 'Politique cookies',
      icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z',
      content: [
        'Lors de votre visite sur Referio, des cookies peuvent être déposés sur votre terminal. Un cookie est un petit fichier texte qui permet de vous reconnaître lors de vos prochaines visites.',
        'Vous pouvez à tout moment modifier vos préférences via les paramètres de votre navigateur.'
      ]
    },
    {
      id: 'securite',
      title: 'Sécurité des données',
      icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
      content: [
        'Referio met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte, destruction ou divulgation accidentelle.',
        'Ces mesures incluent notamment : chiffrement TLS des communications, hachage des mots de passe, accès restreint aux données par rôle, journalisation des accès, sauvegardes régulières chiffrées.',
        'En cas de violation de données susceptible d\'engendrer un risque pour vos droits et libertés, nous nous engageons à notifier l\'APD dans les 72 heures et à vous en informer sans délai indu si le risque est élevé.'
      ]
    },
    {
      id: 'modifications',
      title: 'Modifications de la politique',
      icon: 'M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      content: [
        'La présente politique de confidentialité peut être mise à jour pour refléter les évolutions légales, réglementaires ou liées à nos services.',
        'En cas de modification substantielle, nous vous en informerons par email ou par une notification visible sur le site au moins <strong>15 jours avant</strong> l\'entrée en vigueur des changements.',
        'La date de dernière mise à jour est toujours indiquée en haut de cette page. Nous vous encourageons à la consulter régulièrement.'
      ]
    }
  ];

  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
